import { fetchAccountInfo } from '../accountInfo';
import { clearMtmWidgetSessionCache } from './mtmWidgetSession';
import { extract3dlookDataRecord, parse3dlookJsonRecord } from './parse3dlookAccountData';
import {
  AUTH_STATE_CHANGE_EVENT,
  getAuthenticatedAccountId,
  getAuthenticatedUser,
  isAuthenticatedSession,
  storeAuthenticatedUser,
} from '../../security';

const MEASUREMENT_USER_SESSION_KEY = 'aistylefit_measurement_user_session';
export const MEASUREMENT_PROFILE_UPDATED_EVENT = 'aistylefit:measurement-profile-updated';

export type MeasurementUserSnapshot = {
  email: string;
  firstName: string | null;
  lastName: string | null;
  fullName: string | null;
  heightCm: number | null;
  weightKg: number | null;
  gender: string | null;
  accountId: string | null;
  personId: string | null;
  savedAt: string;
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function readString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function readPositiveInteger(value: unknown): number | null {
  const parsed = Number.parseInt(String(value ?? ''), 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function readPositiveNumber(value: unknown): number | null {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function isValidEmail(value: string): boolean {
  return value.length > 3 && value.includes('@') && value.includes('.');
}

function parseJsonRecord(value: unknown): Record<string, unknown> | null {
  return parse3dlookJsonRecord(value);
}

function readPersonIdFromRecord(record: Record<string, unknown>): string {
  return readString(
    record.person_id
    ?? record.personId
    ?? record.look_person_id
    ?? record.lookPersonId,
  );
}

export function extractPersonIdFromUser(user: unknown): string {
  if (!isRecord(user)) {
    return '';
  }

  const directCandidates = [
    user.person_id,
    user.personId,
    user.look_person_id,
    user.lookPersonId,
  ];

  for (const candidate of directCandidates) {
    const value = readString(candidate);
    if (value) {
      return value;
    }
  }

  const nestedSources = [
    user.data_3dlook,
    user.data3dlook,
    user.data_3dlook_json,
    user.data3dlookJson,
    user.measurement_profile,
    user.measurementProfile,
    user.fit_profile,
    user.fitProfile,
    user.account,
    user.profile,
  ];

  for (const source of nestedSources) {
    const record = parseJsonRecord(source);
    if (!record) {
      continue;
    }

    const personId = readPersonIdFromRecord(record);
    if (personId) {
      return personId;
    }
  }

  return '';
}

export function resolveEmailFromUser(user: unknown, fallbackEmail = ''): string {
  const candidates: string[] = [];

  if (fallbackEmail) {
    candidates.push(fallbackEmail);
  }

  if (isRecord(user)) {
    candidates.push(
      readString(user.email),
      readString(user.user_email),
      readString(user.userEmail),
      readString(user.confirmed_email),
      readString(user.confirmedEmail),
      readString(user.username),
      readString(user.login),
      readString(user.login_id),
      readString(user.loginId),
    );

    if (isRecord(user.account)) {
      candidates.push(
        readString(user.account.email),
        readString(user.account.username),
      );
    }

    if (isRecord(user.profile)) {
      candidates.push(readString(user.profile.email));
    }
  }

  return candidates.find((candidate) => isValidEmail(candidate)) ?? '';
}

function readNameParts(user: Record<string, unknown>) {
  const firstName = readString(
    user.first_name ?? user.firstName ?? user.given_name ?? user.givenName,
  );
  const lastName = readString(
    user.last_name ?? user.lastName ?? user.family_name ?? user.familyName,
  );
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return {
    firstName: firstName || null,
    lastName: lastName || null,
    fullName: fullName || readString(user.full_name ?? user.fullName) || null,
  };
}

function readProfileSources(...records: Array<Record<string, unknown> | null | undefined>) {
  return records.filter((record): record is Record<string, unknown> => Boolean(record));
}

function readProfileInteger(
  sources: Record<string, unknown>[],
  keys: string[],
): number | null {
  for (const source of sources) {
    for (const key of keys) {
      const value = readPositiveInteger(source[key]);
      if (value) {
        return value;
      }
    }

    const nestedSources = [source.measurements, source.state, source.body, source.data]
      .map((entry) => parseJsonRecord(entry))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry));

    for (const nested of nestedSources) {
      for (const key of keys) {
        const value = readPositiveInteger(nested[key]);
        if (value) {
          return value;
        }
      }
    }
  }

  return null;
}

function readProfileNumber(
  sources: Record<string, unknown>[],
  keys: string[],
): number | null {
  for (const source of sources) {
    for (const key of keys) {
      const value = readPositiveNumber(source[key]);
      if (value) {
        return value;
      }
    }

    const nestedSources = [source.measurements, source.state, source.body, source.data]
      .map((entry) => parseJsonRecord(entry))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry));

    for (const nested of nestedSources) {
      for (const key of keys) {
        const value = readPositiveNumber(nested[key]);
        if (value) {
          return value;
        }
      }
    }
  }

  return null;
}

function readProfileGender(sources: Record<string, unknown>[]): string | null {
  for (const source of sources) {
    const directGender = readString(source.user_gender ?? source.gender ?? source.sex);
    if (directGender) {
      return directGender;
    }

    const nestedSources = [source.measurements, source.state, source.body, source.data]
      .map((entry) => parseJsonRecord(entry))
      .filter((entry): entry is Record<string, unknown> => Boolean(entry));

    for (const nested of nestedSources) {
      const nestedGender = readString(nested.user_gender ?? nested.gender ?? nested.sex);
      if (nestedGender) {
        return nestedGender;
      }
    }
  }

  return null;
}

export function buildMeasurementUserSnapshot(
  user: unknown,
  options: { fallbackEmail?: string } = {},
): MeasurementUserSnapshot | null {
  const email = resolveEmailFromUser(user, readString(options.fallbackEmail));
  if (!email) {
    return null;
  }

  const record = isRecord(user) ? user : {};
  const names = readNameParts(record);
  const profileRecord = [record, record.account, record.profile, record.user].find(isRecord) ?? record;
  const data3dlookRecord = extract3dlookDataRecord(record)
    ?? (isRecord(record.account) ? extract3dlookDataRecord(record.account) : null)
    ?? (isRecord(record.profile) ? extract3dlookDataRecord(record.profile) : null);
  const profileSources = readProfileSources(record, profileRecord, data3dlookRecord);

  const heightCm = readProfileInteger(profileSources, [
    'height',
    'user_height',
    'height_cm',
    'heightCm',
    'stature',
  ]);

  const weightKg = readProfileNumber(profileSources, [
    'weight',
    'user_weight',
    'weight_kg',
    'weightKg',
    'body_weight',
    'mass',
  ]);

  const gender = readProfileGender(profileSources);

  const accountId = getAuthenticatedAccountId(record) || getAuthenticatedAccountId() || null;
  const personId = extractPersonIdFromUser(record) || null;

  return {
    email,
    firstName: names.firstName,
    lastName: names.lastName,
    fullName: names.fullName,
    heightCm,
    weightKg,
    gender,
    accountId,
    personId,
    savedAt: new Date().toISOString(),
  };
}

function readStorageValue(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.sessionStorage.getItem(key) ?? window.localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStorageValue(key: string, value: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // Ignore quota errors.
  }
}

function removeStorageValue(key: string) {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // Ignore storage errors.
  }
}

export function getPersistedMeasurementUserSnapshot(): MeasurementUserSnapshot | null {
  const rawValue = readStorageValue(MEASUREMENT_USER_SESSION_KEY);
  if (!rawValue) {
    return null;
  }

  try {
    const parsed = JSON.parse(rawValue) as MeasurementUserSnapshot;
    if (!parsed || typeof parsed.email !== 'string' || !isValidEmail(parsed.email)) {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

export function persistMeasurementUserSnapshot(
  user: unknown,
  options: { fallbackEmail?: string } = {},
): MeasurementUserSnapshot | null {
  const snapshot = buildMeasurementUserSnapshot(user, options);
  if (!snapshot) {
    return getPersistedMeasurementUserSnapshot();
  }

  const existing = getPersistedMeasurementUserSnapshot();
  const merged: MeasurementUserSnapshot = {
    email: snapshot.email,
    firstName: snapshot.firstName ?? existing?.firstName ?? null,
    lastName: snapshot.lastName ?? existing?.lastName ?? null,
    fullName: snapshot.fullName ?? existing?.fullName ?? null,
    heightCm: snapshot.heightCm ?? existing?.heightCm ?? null,
    weightKg: snapshot.weightKg ?? existing?.weightKg ?? null,
    gender: snapshot.gender ?? existing?.gender ?? null,
    accountId: snapshot.accountId ?? existing?.accountId ?? null,
    personId: snapshot.personId ?? existing?.personId ?? null,
    savedAt: existing?.savedAt ?? snapshot.savedAt,
  };

  if (existing && areMeasurementSnapshotsEqual(existing, merged)) {
    return merged;
  }

  merged.savedAt = new Date().toISOString();
  writeStorageValue(MEASUREMENT_USER_SESSION_KEY, JSON.stringify(merged));
  dispatchMeasurementProfileUpdated();
  return merged;
}

function areMeasurementSnapshotsEqual(
  left: MeasurementUserSnapshot,
  right: MeasurementUserSnapshot,
) {
  return (
    left.email === right.email
    && left.firstName === right.firstName
    && left.lastName === right.lastName
    && left.fullName === right.fullName
    && left.heightCm === right.heightCm
    && left.weightKg === right.weightKg
    && left.gender === right.gender
    && left.accountId === right.accountId
    && left.personId === right.personId
  );
}

export function syncAuthUserFromSnapshot(snapshot: MeasurementUserSnapshot) {
  const user = getAuthenticatedUser();
  if (!isRecord(user)) {
    return;
  }

  const nextHeight = snapshot.heightCm ?? user.height;
  const nextWeight = snapshot.weightKg ?? user.weight;
  const nextGender = snapshot.gender ?? user.user_gender ?? user.gender;
  const nextPersonId = snapshot.personId ?? user.person_id ?? user.personId;

  const isUnchanged =
    String(user.email ?? '') === String(snapshot.email ?? '')
    && String(user.height ?? '') === String(nextHeight ?? '')
    && String(user.weight ?? '') === String(nextWeight ?? '')
    && String(user.user_gender ?? user.gender ?? '') === String(nextGender ?? '')
    && String(user.person_id ?? user.personId ?? '') === String(nextPersonId ?? '');

  if (isUnchanged) {
    return;
  }

  storeAuthenticatedUser({
    ...user,
    email: snapshot.email,
    height: nextHeight,
    weight: nextWeight,
    user_gender: nextGender,
    gender: nextGender,
    ...(nextPersonId
      ? {
          person_id: nextPersonId,
          personId: nextPersonId,
        }
      : {}),
  });
}

function dispatchMeasurementProfileUpdated() {
  if (typeof window === 'undefined') {
    return;
  }

  window.dispatchEvent(new Event(MEASUREMENT_PROFILE_UPDATED_EVENT));
}

export function updateMeasurementUserSnapshotFields(
  fields: Partial<Pick<MeasurementUserSnapshot, 'fullName' | 'gender' | 'heightCm' | 'weightKg'>>,
): MeasurementUserSnapshot | null {
  const existing = getMeasurementUserSnapshot();
  if (!existing) {
    return null;
  }

  const fullName = fields.fullName?.trim() ?? existing.fullName;
  const updated: MeasurementUserSnapshot = {
    ...existing,
    fullName: fullName || null,
    firstName: fullName?.trim() || existing.firstName,
    gender: fields.gender ?? existing.gender,
    heightCm: fields.heightCm ?? existing.heightCm,
    weightKg: fields.weightKg ?? existing.weightKg,
    savedAt: new Date().toISOString(),
  };

  writeStorageValue(MEASUREMENT_USER_SESSION_KEY, JSON.stringify(updated));
  clearMtmWidgetSessionCache();
  dispatchMeasurementProfileUpdated();
  return updated;
}

export { getMissingMeasurementFields, isMeasurementProfileReady } from './measurementProfileValidation';

export function clearMeasurementUserSnapshot() {
  removeStorageValue(MEASUREMENT_USER_SESSION_KEY);
  dispatchMeasurementProfileUpdated();
}

function mergeSnapshots(
  fromAuth: MeasurementUserSnapshot | null,
  persisted: MeasurementUserSnapshot | null,
): MeasurementUserSnapshot | null {
  if (!fromAuth && !persisted) {
    return null;
  }

  if (!fromAuth) {
    return persisted;
  }

  if (!persisted) {
    return fromAuth;
  }

  return {
    email: fromAuth.email || persisted.email,
    firstName: fromAuth.firstName ?? persisted.firstName,
    lastName: fromAuth.lastName ?? persisted.lastName,
    fullName: fromAuth.fullName ?? persisted.fullName,
    heightCm: persisted.heightCm ?? fromAuth.heightCm,
    weightKg: persisted.weightKg ?? fromAuth.weightKg,
    gender: persisted.gender ?? fromAuth.gender,
    accountId: fromAuth.accountId ?? persisted.accountId,
    personId: fromAuth.personId ?? persisted.personId,
    savedAt: persisted.savedAt || fromAuth.savedAt,
  };
}

export function getMeasurementUserSnapshot(): MeasurementUserSnapshot | null {
  const persisted = getPersistedMeasurementUserSnapshot();
  const fromAuthUser = buildMeasurementUserSnapshot(getAuthenticatedUser());

  return mergeSnapshots(fromAuthUser, persisted);
}

let ensureSnapshotPromise: Promise<MeasurementUserSnapshot | null> | null = null;
let lastAccountFetchAt = 0;
const ACCOUNT_FETCH_TTL_MS = 30_000;

export async function ensureMeasurementUserSnapshot(
  options: { force?: boolean } = {},
): Promise<MeasurementUserSnapshot | null> {
  if (!isAuthenticatedSession()) {
    clearMeasurementUserSnapshot();
    return null;
  }

  const current = getMeasurementUserSnapshot();
  if (
    !options.force
    && current?.email
    && Date.now() - lastAccountFetchAt < ACCOUNT_FETCH_TTL_MS
  ) {
    return current;
  }

  if (!ensureSnapshotPromise) {
    ensureSnapshotPromise = (async () => {
      try {
        const accountInfo = await fetchAccountInfo();
        lastAccountFetchAt = Date.now();

        if (accountInfo) {
          const snapshot = persistMeasurementUserSnapshot(accountInfo);
          if (snapshot) {
            syncAuthUserFromSnapshot(snapshot);
          }
        } else {
          persistMeasurementUserSnapshot(getAuthenticatedUser());
        }
      } catch {
        persistMeasurementUserSnapshot(getAuthenticatedUser());
      }

      const refreshed = getMeasurementUserSnapshot();
      return refreshed?.email ? refreshed : null;
    })().finally(() => {
      ensureSnapshotPromise = null;
    });
  }

  return ensureSnapshotPromise;
}

export function syncMeasurementUserSessionFromAuth() {
  if (!isAuthenticatedSession()) {
    clearMeasurementUserSnapshot();
    return null;
  }

  return getMeasurementUserSnapshot();
}

let hasInitializedMeasurementSessionCleanup = false;

/** Clears temporary measurement snapshot on logout only (no fetch/persist on login). */
export function initMeasurementSessionCleanup() {
  if (hasInitializedMeasurementSessionCleanup || typeof window === 'undefined') {
    return;
  }

  hasInitializedMeasurementSessionCleanup = true;

  window.addEventListener(AUTH_STATE_CHANGE_EVENT, () => {
    if (!isAuthenticatedSession()) {
      clearMeasurementUserSnapshot();
      clearMtmWidgetSessionCache();
    }
  });
}
